import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GetPlanResponse, AIPlan } from '@notion-training/shared';
import { ApiError, api } from '../../lib/api.js';
import { PlanEmptyHero } from './PlanEmptyHero.js';
import { PlanCard } from './PlanCard.js';
import { PlanLoadingCard } from './PlanLoadingCard.js';
import { PlanErrorCard } from './PlanErrorCard.js';
import { PersonaModal } from './PersonaModal.js';
import { buildPersonaString, type PersonaInput } from './buildPersonaString.js';
import {
  loadLastPersona,
  saveLastPersona,
  isPlanSkipped,
  setPlanSkipped,
  clearPlanSkipped,
} from './personaStorage.js';

export const OPEN_PLAN_MODAL_EVENT = 'notion-training:open-plan-modal';

type Props = { onPlanChange?: (plan: AIPlan | null) => void };

export function PlanSection({ onPlanChange }: Props) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [skippedView, setSkippedView] = useState(isPlanSkipped());
  const [errorKind, setErrorKind] = useState<'rate_limit' | 'generation_failed' | 'load_failed' | null>(null);

  useEffect(() => {
    const onOpen = () => setModalOpen(true);
    window.addEventListener(OPEN_PLAN_MODAL_EVENT, onOpen);

    // Honor the ?openPlan=1 deep-link from the Topbar nudge: if the topbar
    // button was clicked from another page, the URL carries this flag so we
    // open the modal as soon as PlanSection mounts. We strip it after.
    if (new URLSearchParams(window.location.search).get('openPlan') === '1') {
      setModalOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('openPlan');
      window.history.replaceState({}, '', url.toString());
    }

    return () => window.removeEventListener(OPEN_PLAN_MODAL_EVENT, onOpen);
  }, []);

  const planQuery = useQuery<GetPlanResponse>({
    queryKey: ['plan'],
    queryFn: () => api.get<GetPlanResponse>('/api/plan'),
  });

  const generateMutation = useMutation({
    mutationFn: (input: PersonaInput) =>
      api.post<{ plan: AIPlan }>('/api/plan/generate', { persona: buildPersonaString(input) }),
    onSuccess: (data, input) => {
      saveLastPersona(input);
      clearPlanSkipped();
      setSkippedView(false);
      // Refetch /api/plan so we get a fresh remainingToday count
      qc.invalidateQueries({ queryKey: ['plan'] });
      // Optimistically place the new plan in the cache too
      qc.setQueryData<GetPlanResponse>(['plan'], (prev) => ({
        plan: data.plan,
        remainingToday: Math.max(0, (prev?.remainingToday ?? 3) - 1),
      }));
      setModalOpen(false);
      setErrorKind(null);
      onPlanChange?.(data.plan);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        setErrorKind('rate_limit');
      } else {
        setErrorKind('generation_failed');
      }
      setModalOpen(false);
    },
  });

  const planValue = planQuery.data?.plan ?? null;

  // The persona modal is the same in every state — pull it up so any branch
  // that wants to render it just includes {modal}, and the PlanSection's
  // state machine never blocks the modal from opening (especially important
  // when the dashboard hits an /api/plan error: the topbar button still works).
  const modal = modalOpen ? (
    <PersonaModal
      initial={loadLastPersona()}
      onSubmit={(i) => generateMutation.mutate(i)}
      onClose={() => setModalOpen(false)}
    />
  ) : null;

  if (planQuery.isLoading) {
    return <div style={{ minHeight: 60 }} />; // empty placeholder; avoid flicker
  }

  if (planQuery.isError) {
    return (
      <>
        <PlanErrorCard kind="load_failed" />
        {modal}
      </>
    );
  }

  if (generateMutation.isPending) {
    return <PlanLoadingCard />;
  }

  if (errorKind) {
    return (
      <>
        <PlanErrorCard
          kind={errorKind}
          {...(errorKind !== 'rate_limit' && {
            onRetry: () => {
              const last = loadLastPersona();
              if (last) generateMutation.mutate(last);
              else setModalOpen(true);
            },
          })}
          onDismiss={() => setErrorKind(null)}
        />
        {modal}
      </>
    );
  }

  if (planValue) {
    return (
      <>
        <PlanCard
          plan={planValue}
          remainingToday={planQuery.data!.remainingToday}
          onUpdatePlan={() => setModalOpen(true)}
        />
        {modal}
      </>
    );
  }

  if (skippedView) {
    return modal; // an open modal still wins over "I clicked Skip earlier"
  }

  return (
    <>
      <PlanEmptyHero
        onBuild={() => setModalOpen(true)}
        onSkip={() => { setPlanSkipped(); setSkippedView(true); }}
      />
      {modal}
    </>
  );
}
